/**
 * EditSlotModal
 * --------------
 * Allows admins to update an existing booking and trigger resend confirmation emails.
 */
import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  computeSlotLabelAvailability,
  generateAutoSlotLabels,
  parseSlotLabelsInput,
} from '../utils/slotLabelUtils';
import { getEditableMaxPartySize } from '../utils/capacityUtils';

const BOOKING_STATUS_OPTIONS = ['confirmed', 'pending', 'hold', 'cancelled'];

const defaultFormValues = {
  customerName: '',
  customerEmail: '',
  customerPhoneNumber: '',
  partySize: 1,
  slotLabels: '',
  specialNote: '',
  status: 'confirmed',
};

const EditSlotModal = ({
  open,
  onClose,
  room,
  cell,
  booking,
  slotDisplayTime,
  timeSlotValue,
  date,
  onSubmit,
  isSubmitting,
  error,
  slotLabelSuggestions = [],
  onResendEmail,
  isResendingEmail,
  resendError,
  resendSuccessMessage,
}) => {
  const [formValues, setFormValues] = useState(defaultFormValues);
  const [initializedBookingId, setInitializedBookingId] = useState(null);

  const { slotLabelPool, availableSlotLabels, reservedSlotLabels } = useMemo(
    () =>
      computeSlotLabelAvailability({
        cell,
        bookingToExclude: booking,
        slotLabelSuggestions,
      }),
    [cell, booking, slotLabelSuggestions]
  );

  const bookingPartySizeForLimit = booking?.partySize ?? booking?.slotQuantity ?? 1;

  const editableMaxPartySize = useMemo(
    () => getEditableMaxPartySize(cell, booking),
    [cell, booking]
  );

  const partySizeHelperText =
    editableMaxPartySize === null
      ? undefined
      : `Up to ${editableMaxPartySize} smashers remain for this slot.`;

  const partySizeInputProps = useMemo(() => {
    const props = { min: 1 };
    if (editableMaxPartySize !== null) {
      props.max = editableMaxPartySize;
    }
    return props;
  }, [editableMaxPartySize]);

  const currentPartySize = formValues.partySize;

  useEffect(() => {
    if (!open || !booking) {
      setInitializedBookingId(null);
      return;
    }

    const bookingIdentifier = booking.bookingId ?? booking._id ?? booking.id ?? 'booking';
    if (initializedBookingId === bookingIdentifier) {
      return;
    }

    const boundedInitialPartySize =
      editableMaxPartySize === null
        ? bookingPartySizeForLimit
        : Math.min(bookingPartySizeForLimit, editableMaxPartySize);

    const existingSlotLabels = [
      ...parseSlotLabelsInput(booking.slotLabels),
      ...parseSlotLabelsInput(booking.slotLabel),
    ];

    const resolvedSlotLabels =
      existingSlotLabels.length > 0
        ? existingSlotLabels.join(', ')
        : generateAutoSlotLabels({
            partySize: boundedInitialPartySize,
            availableSlotLabels,
            slotLabelPool,
            reservedSlotLabels,
            preferredSlotLabels: existingSlotLabels,
          });

    setFormValues({
      customerName: booking.customerName || 'Guest',
      customerEmail: booking.customerEmail || '',
      customerPhoneNumber: booking.customerPhoneNumber || '',
      partySize: boundedInitialPartySize,
      slotLabels: resolvedSlotLabels,
      specialNote: booking.specialNote || '',
      status: booking.status || 'confirmed',
    });
    setInitializedBookingId(bookingIdentifier);
  }, [
    open,
    booking,
    initializedBookingId,
    editableMaxPartySize,
    bookingPartySizeForLimit,
    availableSlotLabels,
    slotLabelPool,
    reservedSlotLabels,
  ]);

  useEffect(() => {
    if (!open || editableMaxPartySize === null || !currentPartySize) {
      return;
    }
    if (currentPartySize <= editableMaxPartySize) {
      return;
    }

    setFormValues((prev) => {
      const boundedPartySize = Math.min(prev.partySize || 0, editableMaxPartySize);
      const preferredSlotLabels = parseSlotLabelsInput(prev.slotLabels);
      const nextSlotLabels = boundedPartySize
        ? generateAutoSlotLabels({
            partySize: boundedPartySize,
            availableSlotLabels,
            slotLabelPool,
            reservedSlotLabels,
            preferredSlotLabels,
          })
        : '';
      return {
        ...prev,
        partySize: boundedPartySize,
        slotLabels: nextSlotLabels,
      };
    });
  }, [
    open,
    editableMaxPartySize,
    currentPartySize,
    availableSlotLabels,
    slotLabelPool,
    reservedSlotLabels,
  ]);

  const handleDialogClose = (_, reason) => {
    if ((isSubmitting || isResendingEmail) && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    onClose();
  };

  const handleChange = (field) => (event) => {
    const { value } = event.target;

    if (field === 'partySize') {
      setFormValues((prev) => {
        if (value === '') {
          return {
            ...prev,
            partySize: '',
            slotLabels: '',
          };
        }

        const numericValue = Number(value);
        if (Number.isNaN(numericValue)) {
          return prev;
        }
        const upperBound =
          editableMaxPartySize === null
            ? numericValue
            : Math.min(numericValue, editableMaxPartySize);
        const nextPartySize = upperBound > 0 ? upperBound : '';

        const preferredSlotLabels = parseSlotLabelsInput(prev.slotLabels);
        const nextSlotLabels = nextPartySize
          ? generateAutoSlotLabels({
              partySize: nextPartySize,
              availableSlotLabels,
              slotLabelPool,
              reservedSlotLabels,
              preferredSlotLabels,
            })
          : '';

        return {
          ...prev,
          partySize: nextPartySize,
          slotLabels: nextPartySize ? nextSlotLabels : '',
        };
      });
      return;
    }

    if (field === 'slotLabels') {
      setFormValues((prev) => ({
        ...prev,
        slotLabels: value,
      }));
      return;
    }

    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit(formValues);
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle>Edit booking</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {resendError && <Alert severity="error">{resendError}</Alert>}
            {resendSuccessMessage && <Alert severity="success">{resendSuccessMessage}</Alert>}
            <Typography variant="body2">
              Room: <strong>{room?.name || booking?.roomName || 'Unknown room'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Date: {date || booking?.date || 'N/A'} — Time: {slotDisplayTime || timeSlotValue || booking?.timeSlot || 'TBD'}
            </Typography>
            {cell?.sessionLabels?.length ? (
              <Typography variant="caption" color="text.secondary">
                Session: {cell.sessionLabels.join(' • ')}
              </Typography>
            ) : booking?.sessionLabel ? (
              <Typography variant="caption" color="text.secondary">
                Session: {booking.sessionLabel}
              </Typography>
            ) : null}
            <Typography variant="caption" color="text.secondary">
              Booking reference: {booking?.bookingId || booking?._id || booking?.id || 'N/A'}
            </Typography>
            <TextField
              label="Customer name"
              value={formValues.customerName}
              onChange={handleChange('customerName')}
              required
              fullWidth
              autoFocus
            />
            <TextField
              label="Customer email"
              value={formValues.customerEmail}
              onChange={handleChange('customerEmail')}
              type="email"
              fullWidth
            />
            <TextField
              label="Customer phone"
              value={formValues.customerPhoneNumber}
              onChange={handleChange('customerPhoneNumber')}
              fullWidth
            />
            <TextField
              label="Party size"
              value={formValues.partySize}
              onChange={handleChange('partySize')}
              type="number"
              inputProps={partySizeInputProps}
              helperText={partySizeHelperText}
              required
              fullWidth
            />
            <TextField
              label="Slot labels (comma separated)"
              value={formValues.slotLabels}
              onChange={handleChange('slotLabels')}
              helperText={
                slotLabelSuggestions.length
                  ? `Suggestions: ${slotLabelSuggestions.join(', ')}`
                  : 'Optional'
              }
              fullWidth
            />
            <TextField
              label="Booking status"
              value={formValues.status}
              onChange={handleChange('status')}
              select
              fullWidth
            >
              {BOOKING_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </TextField>
            <TextField
              label="Special note"
              value={formValues.specialNote}
              onChange={handleChange('specialNote')}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', justifyContent: 'space-between', gap: 1 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onResendEmail}
            disabled={!onResendEmail || isResendingEmail}
          >
            {isResendingEmail ? 'Resending…' : 'Resend confirmation email'}
          </Button>
          <Box>
            <Button onClick={onClose} disabled={isSubmitting || isResendingEmail} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : 'Save changes'}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditSlotModal;